/**
 * Resolve a localized display string against the active locale.
 *
 * Iter 16 added bilingual support to ``report_drafts``: a label can be
 * either a legacy ``string`` (single language) or a ``LocalizedString``
 * object ``{en, es}``. The render path must accept both shapes without
 * branching at every call site — this helper does the work in one place.
 *
 * Mirror of ``hp_portal.report_engine.i18n.resolve_localized`` (Python).
 * Keep both in sync; same fallback semantics.
 */

export type Locale = "en" | "es" | "pt";

/**
 * Bilingual label shape. The narrow ``{en, es}`` definition keeps
 * existing call sites strongly typed, while the indexed signature
 * leaves the door open for additional locales (``pt``, ``fr``, ...)
 * to land without a schema migration — render code that iterates
 * over ``SUPPORTED_LOCALES`` will pick them up automatically.
 */
export type LocalizedString = {
  en?: string;
  es?: string;
} & {
  [locale: string]: string | undefined;
};

/** Anything that can be passed to ``resolveLocalized``. */
export type DisplayLabel = string | LocalizedString | null | undefined;

/**
 * Locales the editor renders side-by-side in BilingualInput and that
 * ``resolveLocalized`` will fall back through. To add a new locale
 * later: extend this list, optionally extend the ``Locale`` union for
 * stricter typing, and the BilingualInput row grid + fallback chain
 * pick it up automatically. No callers need to change.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "es", "pt"] as const;
export const DEFAULT_LOCALE: Locale = "en";

/** Human-friendly badge text for a locale (shown next to each input). */
export const LOCALE_BADGE: Record<Locale, string> = {
  en: "EN",
  es: "ES",
  pt: "PT",
};

/**
 * Return the display string for ``locale``.
 *
 * Resolution order:
 * 1. ``null`` / ``undefined`` → ``""``.
 * 2. ``string``:
 *    a. If it parses as a JSON object with a supported locale key,
 *       treat as a JSON-stringified ``LocalizedString`` (used by
 *       ``projects.name`` / ``report_training.content`` TEXT
 *       columns) and recurse.
 *    b. Otherwise return verbatim (legacy single-language).
 * 3. ``LocalizedString``:
 *    a. ``value[locale]`` if present and non-empty.
 *    b. ``value.en`` if present and non-empty.
 *    c. First non-empty string across all keys.
 *    d. ``""``.
 */
export function resolveLocalized(
  value: DisplayLabel,
  locale: Locale = DEFAULT_LOCALE,
): string {
  if (value == null) return "";
  if (typeof value === "string") {
    // Cheap prefix check before JSON.parse so plain strings stay
    // zero-cost. Looks for ``{"en"`` or ``{"es"`` at the start
    // (with optional whitespace).
    const trimmed = value.trimStart();
    if (
      trimmed.startsWith("{") &&
      SUPPORTED_LOCALES.some((loc) => trimmed.includes(`"${loc}"`))
    ) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (parsed != null && typeof parsed === "object" && isLocalized(parsed)) {
          return resolveLocalized(parsed as LocalizedString, locale);
        }
      } catch {
        // Not valid JSON — return verbatim below.
      }
    }
    return value;
  }
  if (typeof value === "object") {
    const primary = value[locale];
    if (typeof primary === "string" && primary.length > 0) return primary;
    const en = value.en;
    if (typeof en === "string" && en.length > 0) return en;
    for (const loc of SUPPORTED_LOCALES) {
      const v = value[loc];
      if (typeof v === "string" && v.length > 0) return v;
    }
    for (const v of Object.values(value)) {
      if (typeof v === "string" && v.length > 0) return v;
    }
    return "";
  }
  return String(value);
}

/**
 * Build a ``LocalizedString`` from explicit per-locale strings.
 * Use ``fallback`` to seed empty slots from the legacy single-value
 * source — useful in the migration script and the BilingualInput
 * component when only one side has been edited.
 */
export function makeLocalized(
  en = "",
  es = "",
  fallback = "",
): LocalizedString {
  return {
    en: en || fallback,
    es: es || fallback,
  };
}

/** True iff value is a localized object (has a supported locale key). */
export function isLocalized(value: unknown): value is LocalizedString {
  if (value == null || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return SUPPORTED_LOCALES.some((loc) => loc in obj);
}

/**
 * True iff ``raw`` is a JSON-stringified bilingual object — the
 * ``'{"en": "...", "es": "..."}'`` shape some SQL columns emit (e.g.
 * gating_executive's pattern column). Mirrors the string branch of
 * ``resolveLocalized``: cheap prefix check first, ``JSON.parse`` only
 * when the string plausibly matches. Used to exclude such cells from
 * behaviors that need a plain scalar (e.g. cross-filter clicks).
 */
export function isBilingualJsonString(raw: unknown): boolean {
  if (typeof raw !== "string") return false;
  const trimmed = raw.trimStart();
  if (
    !trimmed.startsWith("{") ||
    !SUPPORTED_LOCALES.some((loc) => trimmed.includes(`"${loc}"`))
  ) {
    return false;
  }
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return isLocalized(parsed);
  } catch {
    return false;
  }
}

/**
 * Coerce any input to a LocalizedString covering at least the
 * supported locales. Strings become the same value seeded into every
 * supported slot (so render code sees something regardless of locale).
 * Existing objects pass through and gain any missing locale keys as
 * ``""``. ``null`` / ``undefined`` produce an all-empty record.
 *
 * Used by ``BilingualInput`` on mount to normalize legacy strings
 * before the user starts editing the inputs.
 */
export function toLocalized(value: DisplayLabel): LocalizedString {
  if (value == null) {
    const empty: LocalizedString = {};
    for (const loc of SUPPORTED_LOCALES) empty[loc] = "";
    return empty;
  }
  if (typeof value === "string") {
    // A legacy single-language string is seeded into EN and ES, as it was
    // before PT existed. PT stays empty: copying the (usually Spanish)
    // text there would store it as a Portuguese translation, and the
    // "shown in another language" notice (D2) would never fire.
    const seeded: LocalizedString = {};
    for (const loc of SUPPORTED_LOCALES) seeded[loc] = loc === "pt" ? "" : value;
    return seeded;
  }
  const filled: LocalizedString = { ...value };
  for (const loc of SUPPORTED_LOCALES) {
    if (typeof filled[loc] !== "string") filled[loc] = "";
  }
  return filled;
}
